import { QuoteMediaBrowserHistoryClient, validateHistoryGrant } from './quotemedia-history-client.js';

export class HistoryDataSource {
  constructor(endpoint = null, { browserHistory = false,
    csrfToken = globalThis.document?.querySelector?.('meta[name="csrf-token"]')?.content || null } = {}) {
    this.browserHistory = browserHistory;
    this.csrfToken = csrfToken;
    this.provider = new QuoteMediaBrowserHistoryClient();
    this.endpoint = null;
    if (endpoint) this.setEndpoint(endpoint);
  }

  setEndpoint(endpoint) {
    const normalized = normalizeEndpoint(endpoint, "Market-data history");
    this.endpoint = normalized;
  }

  async load({ symbolId, instrumentId, symbol, timeframe, count, endDate = null, includeCurrent = true, signal }) {
    if (!this.endpoint) {
      throw new TypeError("Market-data history requires a product-qualified endpoint.");
    }
    if (this.browserHistory) {
      const startedAt = performance.now();
      const request = { symbolId, instrumentId, symbol, timeframe, count, endDate, includeCurrent };
      let grant = await this.requestGrant(request, signal);
      if (grant.transport === 'Browser') {
        validateHistoryGrant(grant);
        let result;
        try { result = await this.provider.load(grant, { ...request, signal }); }
        catch (error) {
          if (signal?.aborted || error.reason !== 'provider_auth_rejected') throw error;
          grant = await this.requestGrant({ ...request, rejectedSid: grant.sid }, signal);
          validateHistoryGrant(grant);
          result = await this.provider.load(grant, { ...request, signal });
        }
        return { ...result, duration: performance.now() - startedAt };
      }
      if (grant.transport !== 'Server') throw new Error('Chart history transport is unavailable.');
    }
    const query = new URLSearchParams({
      symbol,
      timeframe,
      count: String(count)
    });
    if (symbolId) {
      query.set("symbolId", symbolId);
    } else if (instrumentId) {
      query.set("instrumentId", instrumentId);
    }
    if (endDate) query.set("endDate", String(endDate));
    if (includeCurrent === false) query.set("includeCurrent", "false");
    const startedAt = performance.now();
    const response = await fetch(`${this.endpoint}?${query}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal
    });
    const duration = performance.now() - startedAt;
    if (!response.ok) {
      let problem = null;
      try { problem = await response.json(); } catch { /* Non-JSON errors keep the status fallback. */ }
      const reason = typeof problem?.reason === "string" ? problem.reason : null;
      const customerMessage = response.status === 403 || response.status === 503
        ? accessFailureMessage(reason)
        : null;
      const error = new Error(customerMessage || `Market-data request failed with status ${response.status}.`);
      error.name = "MarketDataRequestError";
      error.status = response.status;
      if (customerMessage) {
        error.reason = reason;
        error.customerMessage = customerMessage;
      }
      throw error;
    }
    return {
      bars: await response.json(),
      duration,
      exhausted: historyExhausted(response.headers?.get?.("X-FlexCharting-History-Exhausted")),
      nextEndDate: historyCursor(response.headers?.get?.("X-FlexCharting-History-Next-End-Date"))
    };
  }

  async requestGrant(request, signal) {
    const response = await fetch(`${this.endpoint}/request-grant`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', signal,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json',
        ...(this.csrfToken ? { 'X-CSRF-TOKEN': this.csrfToken } : {}) }, body: JSON.stringify(request)
    });
    if (!response.ok) {
      let problem;
      try { problem = await response.json(); } catch { /* Keep errors free of provider details. */ }
      const reason = typeof problem?.reason === 'string' ? problem.reason : null;
      const customerMessage = [403, 503].includes(response.status) ? accessFailureMessage(reason) : null;
      const error = new Error(customerMessage || `Market-data authorization failed with status ${response.status}.`);
      throw Object.assign(error, { name: 'MarketDataRequestError', status: response.status, reason,
        ...(customerMessage ? { customerMessage } : {}) });
    }
    return response.json();
  }
}

function historyExhausted(value) {
  return value === 'true' ? true : value === 'false' ? false : undefined;
}

function historyCursor(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function accessFailureMessage(reason) {
  switch (reason) {
    case "provider_user_inactive":
      return "QuoteMedia access needs to be refreshed. Open your market-data setup, check your access, then retry the chart.";
    case "provider_user_required":
    case "aggregate_entitlement_not_ready":
    case "provider_products_inconsistent":
    case "provider_credential_mode_unknown":
      return "Your QuoteMedia setup is not ready. Open your market-data setup to check your access, then retry the chart.";
    case "commercial_chart_access_required":
    case "active_market_data_entitlement_required":
      return "Current chart and market-data access is required. Check your subscription and market-data setup, then retry the chart.";
    case "provider_authentication_unavailable":
    case "provider_status_refresh_unavailable":
      return "QuoteMedia could not confirm your access right now. Please retry shortly.";
    default:
      return null;
  }
}

function normalizeEndpoint(endpoint, label) {
  const normalized = String(endpoint || "").trim().replace(/\/+$/, "");
  if (!normalized.startsWith("/") || normalized.startsWith("//") || normalized.includes("\\")) {
    throw new TypeError(`${label} endpoint must be an application-relative URL.`);
  }
  return normalized;
}
